"use client";
import { useRovingTabs } from "@/hooks/useRovingTabs.hook";
import {
  pillTabClass,
  PillTabScroller,
  WhatsAppPhone,
} from "@/components/marketing";
import type { WhatsAppTab } from "../../data";

interface WhatsAppViewerProps {
  ariaLabel: string;
  tablistLabel: string;
  chat: { initial: string; shop: string; subtitle: string };
  tabs: WhatsAppTab[];
}

// The preview's `whatsapp_viewer`: a wrapping row of pill tabs, one per
// message type. The active tab shows the message in a WhatsApp phone beside
// a note on when it is sent.
const WhatsAppViewer = ({
  ariaLabel,
  tablistLabel,
  chat,
  tabs,
}: WhatsAppViewerProps) => {
  const viewer = useRovingTabs(tabs.length);
  const tab = tabs[viewer.activeIndex];

  return (
    <figure aria-label={ariaLabel}>
      <PillTabScroller ariaLabel={tablistLabel}>
        {tabs.map((item, index) => {
          const isActive = index === viewer.activeIndex;
          return (
            <button
              key={item.label}
              type="button"
              role="tab"
              id={`wa-tab-${index}`}
              aria-selected={isActive}
              aria-controls="wa-panel"
              tabIndex={isActive ? 0 : -1}
              onClick={() => viewer.select(index)}
              onKeyDown={(event) => viewer.onTabKeyDown(event, index)}
              className={pillTabClass(isActive)}
            >
              {item.label}
            </button>
          );
        })}
      </PillTabScroller>
      <div
        // Remount on tab change so the panel replays its entrance.
        key={viewer.activeIndex}
        id="wa-panel"
        role="tabpanel"
        aria-labelledby={`wa-tab-${viewer.activeIndex}`}
        className="mx-auto mt-6 grid max-w-[820px] items-center gap-8 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 motion-safe:animation-duration-400 md:grid-cols-[auto_1fr] md:gap-[34px]"
      >
        <div className="w-[272px] max-w-full justify-self-center">
          <WhatsAppPhone chat={chat} messages={tab.messages} />
        </div>
        <div className="grid gap-2">
          <p className="text-[13px] font-bold text-ink-muted">{tab.whenLabel}</p>
          <h3 className="text-[1.4rem] leading-tight font-bold">{tab.when}</h3>
          <p className="text-base leading-relaxed text-ink-muted">{tab.text}</p>
        </div>
      </div>
    </figure>
  );
};

export default WhatsAppViewer;
